const util = require('util');
const promiseMap = require('p-map');
const { getLanguageCode } = require('../../../utils/contentfulModules');
const { getSkillsFilter } = require('../../../utils/algoliaUtils.cjs');
const { SECURE_PREVIEW_DIRECTORY } = require('../../../utils/preview');

const DEFAULT_CONCURRENCY_FOR_ITERATION = 15;

function getLearnPagePath(languageCode, active, slug) {
  if (slug) {
    const formattedSlug = slug.toLowerCase().trim();
    const langPrefix = languageCode === 'es' ? 'es/' : '';
    const path = languageCode === 'es' ? 'aprende' : 'learn';
    const previewPrefix = !active ? `${SECURE_PREVIEW_DIRECTORY}/` : '';
    const learnRootURL = `/${langPrefix}${previewPrefix}${path}`;
    return `${learnRootURL}/${formattedSlug}`;
  }

  return null;
}

function getCategoryTitleOverridesMap(learnPages) {
  return learnPages
    .filter((learnPage) => !!learnPage.category)
    .reduce(
      (acc, { category, title }) => acc.set(category, title),
      new Map(),
    );
}

function getSubcategoryRecords(learnPages) {
  return learnPages
    .filter((learnPage) => !!learnPage.subcategory)
    .map((subcategoryLearnPage) => {
      const originalSubcategoryTitle = subcategoryLearnPage.subcategory;
      const languageCode = getLanguageCode(subcategoryLearnPage.seo);
      const path = getLearnPagePath(
        languageCode,
        subcategoryLearnPage.active,
        subcategoryLearnPage.slug,
      );
      return {
        title: subcategoryLearnPage.title || originalSubcategoryTitle,
        originalSubcategoryTitle,
        path,
        active: subcategoryLearnPage.active,
      };
    });
}

function extractCategoryProductCount(facets, subcategory) {
  return Object.entries(facets['skills.subcategory'])
    .find(([subcategoryName]) => subcategoryName === subcategory)
    .at(1);
}

function extractParentCategory(hits, subcategory) {
  return hits
    ?.at(0)
    .skills.find((skill) => skill.subcategory === subcategory)
    .category;
}

async function addCountAndCategoryToSubcategoryRecords(subcategoryRecords, algoliaClient) {
  return promiseMap(subcategoryRecords, async (subcategoryRecord) => {
    try {
      const { hits, facets } = await algoliaClient
        .search('', {
          responseFields: ['hits', 'facets'],
          facets: ['skills.subcategory'],
          hitsPerPage: 1,
          filters: getSkillsFilter(null, subcategoryRecord.originalSubcategoryTitle),
        });

      return {
        ...subcategoryRecord,
        count: extractCategoryProductCount(facets, subcategoryRecord.originalSubcategoryTitle),
        category: extractParentCategory(hits, subcategoryRecord.originalSubcategoryTitle),
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(util.inspect(err, { depth: null }));
      // 👆 because algolia errors have under-reported due to deep object nesting
      throw err;
    }
  }, { concurrency: DEFAULT_CONCURRENCY_FOR_ITERATION });
}

/**
 * Function intended to be on {@link addCountAndCategoryToSubcategoryRecords}  return data.
 */
function getUniqueCategoriesFromSubcategoryResults(fullSubcategoryResults) {
  return Array.from(
    fullSubcategoryResults.reduce((set, { category }) => set.add(category), new Set()),
  );
}

function prepareLearnIndexPageProps(categories, categoryTitleOverridesMap, subcategoryResults) {
  return categories
    .map(category => ({
      title: categoryTitleOverridesMap.get(category) || category,
      subcategoryPages: subcategoryResults
        .filter(subcategoryResult => subcategoryResult.category === category),
    }))
    .filter(categoryPage => !!categoryPage.subcategoryPages?.at(0))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/* Learn Page Utils */
function createLearnPageSkillsMap(learnPages) {
  return learnPages
    .map(learnPage => {
      const originalSkillTitle = learnPage.category || learnPage.subcategory || learnPage.skill;
      const languageCode = getLanguageCode(learnPage.seo);
      const path = getLearnPagePath(languageCode, learnPage.active, learnPage.slug);
      return {
        title: learnPage.shortPageTitle || learnPage.title || originalSkillTitle,
        originalSkillTitle,
        path,
        active: learnPage.active,
      };
    })
    .filter(skillRecord => skillRecord.title && skillRecord.path)
    .reduce((acc, skill) => acc.set(skill.originalSkillTitle, skill), new Map());
}

async function getFacetsForLearnPages(learnPages, algoliaClient) {
  return promiseMap(learnPages, async learnPage => {
    try {
      const filters = getSkillsFilter(learnPage?.category, learnPage?.subcategory, learnPage?.skill);

      const facets = await algoliaClient.search('', {
        responseFields: ['facets'],
        filters,
        facets: ['skills.category', 'skills.subcategory', 'skills.skill'],
      });

      return {
        ...learnPage,
        facets,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(util.inspect(err, { depth: null }));
      // 👆 because algolia errors have under-reported due to deep object nesting
      throw err;
    }
  }, { concurrency: DEFAULT_CONCURRENCY_FOR_ITERATION });
}

function addRelatedSkillsToLearnPages(learnPages, allSkillsWithLearnPagesMap) {
  return learnPages.map((learnPage) => {
    // returns an object like {'skills.skill': {'Apache Hadoop': 2, ...}, 'skills.category': {Analysis: 1, ...}, 'skills.subcategory': {Apache: 2, ...}}
    const allFacetsByCategory = learnPage.facets.facets;
    // returns an object like {'Apache Hadoop': 2, Analysis: 1, ...}
    const allFacetsGrouped = Object.entries(allFacetsByCategory).reduce((acc, val) => ({ ...acc, ...val[1] }), {});
    const allSkillNames = Object.keys(allFacetsGrouped).sort((a, b) => allFacetsGrouped[b] - allFacetsGrouped[a]);
    const skills = allSkillNames.map(skillName => allSkillsWithLearnPagesMap.get(skillName)).filter(Boolean);
    delete learnPage.facets;
    return {
      ...learnPage,
      relatedSkills: skills,
    };
  })
};

module.exports = {
  getLearnPagePath,
  getCategoryTitleOverridesMap,
  getSubcategoryRecords,
  addCountAndCategoryToSubcategoryRecords,
  getUniqueCategoriesFromSubcategoryResults,
  prepareLearnIndexPageProps,
  createLearnPageSkillsMap,
  getFacetsForLearnPages,
  addRelatedSkillsToLearnPages,
};
